<template>
  <AdminAppLayout title="Leave Applications">
    <div class="container" style="margin-top: 30px; margin-bottom: 50px; width: 95%; max-width: 1400px;">
      
      <div class="valign-wrapper" style="margin-bottom: 10px;">
        <i class="material-icons medium indigo-text text-darken-3" style="margin-right: 15px;">fact_check</i>
        <h4 class="grey-text text-darken-3 bold" style="margin: 0;">Leave Applications Management</h4>
      </div>
      <p class="grey-text text-darken-1" style="margin-bottom: 30px; font-size: 1.1rem;">
        Review pending applications and track the approval history within your jurisdiction.
      </p>

      <!-- ========================================================================================= -->
      <!-- LOADING STATE (SKELETONS) -->
      <!-- ========================================================================================= -->
      <div v-if="loading">
        <div class="row" style="margin-bottom: 20px;">
          <div class="col s12" style="padding: 0;">
            <div style="display: flex; border-bottom: 2px solid #e0e0e0; height: 48px; align-items: center; margin-bottom: 15px;">
              <div style="flex: 1;"><div class="skeleton" style="height: 20px; width: 50%; margin: 0 auto; border-radius: 4px;"></div></div>
              <div style="flex: 1;"><div class="skeleton" style="height: 20px; width: 50%; margin: 0 auto; border-radius: 4px;"></div></div>
            </div>
          </div>
        </div>

        <div class="col s12" style="padding: 0;">
          <div class="card z-depth-1" style="border-radius: 8px; border: 1px solid #e0e0e0;">
            <div class="card-content" style="padding: 0;">
              
              <!-- DESKTOP SKELETON TABLE -->
              <div class="hide-on-med-and-down">
                <table class="highlight responsive-table">
                  <thead class="grey lighten-4">
                    <tr>
                      <th style="padding-left: 24px;">Date Filed</th>
                      <th>Applicant Name</th>
                      <th>Leave Type</th>
                      <th>Inclusive Dates</th>
                      <th>Working Days</th>
                      <th class="center-align" style="padding-right: 24px;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="n in 5" :key="'skel-desk-' + n">
                      <td style="padding-left: 24px;"><div class="skeleton" style="width: 80px; height: 16px; border-radius: 4px;"></div></td>
                      <td><div class="skeleton" style="width: 150px; height: 16px; border-radius: 4px;"></div></td>
                      <td><div class="skeleton" style="width: 100px; height: 24px; border-radius: 12px;"></div></td>
                      <td><div class="skeleton" style="width: 180px; height: 16px; border-radius: 4px;"></div></td>
                      <td class="center-align"><div class="skeleton" style="width: 30px; height: 16px; border-radius: 4px; margin: 0 auto;"></div></td>
                      <td class="center-align" style="padding-right: 24px;">
                        <div class="skeleton" style="width: 110px; height: 32px; border-radius: 4px; margin: 0 auto;"></div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- MOBILE SKELETON CARDS -->
              <div class="hide-on-large-only" style="padding: 15px;">
                <div style="display: flex; flex-direction: column; gap: 15px;">
                  <div v-for="n in 4" :key="'skel-mob-' + n" class="card z-depth-0" style="margin: 0; border-radius: 8px; border: 1px solid #e0e0e0;">
                    <div class="card-content" style="padding: 15px;">
                      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px;">
                        <div class="skeleton" style="width: 45%; height: 24px; border-radius: 4px;"></div>
                        <div class="skeleton" style="width: 25%; height: 14px; border-radius: 4px;"></div>
                      </div>
                      <div class="skeleton" style="width: 70%; height: 14px; border-radius: 4px; margin-bottom: 12px;"></div>
                      <div class="skeleton" style="width: 55%; height: 14px; border-radius: 4px; margin-bottom: 12px;"></div>
                      <div class="skeleton" style="width: 40%; height: 14px; border-radius: 4px;"></div>
                    </div>
                    <div class="card-action" style="padding: 10px 15px; background-color: #f9f9f9; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;">
                      <div class="skeleton" style="width: 100%; height: 36px; border-radius: 4px;"></div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- ========================================================================================= -->
      <!-- LOADED DATA STATE -->
      <!-- ========================================================================================= -->
      <div v-else>
        <div class="row" style="margin-bottom: 20px;">
          <div class="col s12" style="padding: 0;">
            <ul class="tabs" ref="adminTabs" style="background-color: transparent; border-bottom: 2px solid #e0e0e0;">
              <li class="tab col s6">
                <a href="#pending-queue" class="active indigo-text text-darken-3 bold">
                  Action Required
                  <span v-if="approverQueue.length > 0" class="new badge red" data-badge-caption="" style="margin-left: 8px; border-radius: 4px;">
                    {{ approverQueue.length }}
                  </span>
                </a>
              </li>
              <li class="tab col s6">
                <a href="#my-history" class="indigo-text text-darken-3 bold">My Approval History</a>
              </li>
            </ul>
          </div>
        </div>

        <div id="pending-queue" class="col s12" style="padding: 0;">
          <div class="card z-depth-1" style="border-radius: 8px; border: 1px solid #e0e0e0;">
            <div class="card-content" style="padding: 0;">
              
              <!-- DESKTOP TABLE -->
              <div class="hide-on-med-and-down">
                <table class="highlight responsive-table">
                  <thead class="white">
                    <tr>
                      <th style="padding-left: 24px;">Date Filed</th>
                      <th>Applicant Name</th>
                      <th>Leave Type</th>
                      <th>Inclusive Dates</th>
                      <th>Working Days</th>
                      <th class="center-align" style="padding-right: 24px;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="approverQueue.length === 0">
                      <td colspan="6" class="center-align" style="padding: 50px 20px;">
                        <i class="material-icons grey-text text-lighten-1" style="font-size: 4rem; display: block; margin-bottom: 10px;">done_all</i>
                        <h6 class="grey-text text-darken-1 bold">You're all caught up!</h6>
                        <p class="grey-text">There are no leave applications waiting for your approval at this moment.</p>
                      </td>
                    </tr>
                    <tr v-for="app in approverQueue" :key="app.id">
                      <td style="padding-left: 24px; white-space: nowrap;">{{ formatDate(app.date_of_filing) }}</td>
                      <td class="bold">{{ app.employee ? app.employee.first_name : '' }} {{ app.employee ? app.employee.last_name : '' }}</td>
                      <td><div class="chip green lighten-5 green-text text-darken-4 bold" style="border-radius: 4px; margin: 0;">{{ app.leave_type }}</div></td>
                      <td>{{ app.inclusive_dates }}</td>
                      <td class="center-align bold">{{ app.working_days }}</td>
                      <td class="center-align" style="padding-right: 24px; white-space: nowrap;">
                        
                        <button @click="$router.push(`/leave-action/${app.id}`)" class="btn-small indigo darken-3 waves-effect waves-light" style="border-radius: 4px; padding: 0 15px;">
                          <i class="material-icons left" style="margin-right: 5px;">edit_document</i> Take Action
                        </button>

                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- MOBILE CARDS (PENDING QUEUE) -->
              <div class="hide-on-large-only" style="padding: 15px;">
                <div v-if="approverQueue.length === 0" class="center-align" style="padding: 30px; border: 1px dashed #ccc; border-radius: 8px;">
                  <i class="material-icons grey-text text-lighten-1" style="font-size: 3rem; display: block; margin-bottom: 10px;">done_all</i>
                  <h6 class="grey-text text-darken-1 bold">You're all caught up!</h6>
                  <p class="grey-text" style="font-size: 0.9rem;">No applications waiting for your approval.</p>
                </div>
                
                <div v-else style="display: flex; flex-direction: column; gap: 15px;">
                  <div v-for="app in approverQueue" :key="'mob-app-' + app.id" class="card z-depth-0" style="margin: 0; border-radius: 8px; border: 1px solid #e0e0e0;">
                    <div class="card-content" style="padding: 15px;">
                      
                      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
                        <div class="valign-wrapper bold" style="flex: 1; padding-right: 10px;">
                          <i v-if="app.leave_type.includes('Vacation')" class="material-icons green-text text-darken-1" style="margin-right: 8px;">work_outline</i>
                          <i v-else-if="app.leave_type.includes('Sick')" class="material-icons blue-text text-darken-1" style="margin-right: 8px;">local_hospital</i>
                          <i v-else class="material-icons orange-text text-darken-1" style="margin-right: 8px;">event</i>
                          <span style="font-size: 1.05rem; color: #1565c0; line-height: 1.2;">{{ app.leave_type }}</span>
                        </div>
                        <span class="grey-text" style="font-size: 0.8rem; white-space: nowrap;">{{ formatDate(app.date_of_filing) }}</span>
                      </div>
                      
                      <p style="margin-bottom: 5px; font-size: 0.95rem;"><b>Applicant: </b> <span style="font-weight: 500;">{{ app.employee ? app.employee.first_name : '' }} {{ app.employee ? app.employee.last_name : '' }}</span></p>
                      
                      <div class="grey-text text-darken-2 valign-wrapper" style="font-size: 0.95rem; margin-bottom: 5px;">
                        <i class="material-icons left" style="font-size: 1.1rem; margin-right: 5px; color: #757575;">date_range</i> 
                        {{ app.inclusive_dates }}
                      </div>
                      
                      <div class="grey-text text-darken-2 valign-wrapper" style="font-size: 0.95rem;">
                        <i class="material-icons left" style="font-size: 1.1rem; margin-right: 5px; color: #757575;">schedule</i> 
                        {{ app.working_days }} Working Day(s)
                      </div>

                    </div>
                    
                    <div class="card-action" style="padding: 10px 15px; background-color: #f9f9f9; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; display: flex; justify-content: flex-end; gap: 10px;">
                      <button @click="$router.push(`/leave-action/${app.id}`)" class="btn-small indigo darken-3 waves-effect waves-light mobile-full-btn" style="border-radius: 4px; box-shadow: none;">
                        <i class="material-icons left" style="margin-right: 5px; font-size: 1rem;">edit_document</i> Take Action
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div id="my-history" class="col s12" style="padding: 0; display: none;">
          <div class="card z-depth-1" style="border-radius: 8px; border: 1px solid #e0e0e0;">
            <div class="card-content" style="padding: 0;">
              
              <!-- DESKTOP TABLE -->
              <div class="hide-on-med-and-down">
                <table class="highlight responsive-table">
                  <thead class="white">
                    <tr>
                      <th style="padding-left: 24px;">Date Filed</th>
                      <th>Applicant Name</th>
                      <th>Leave Type</th>
                      <th>Dates</th>
                      <th>Current Stage</th>
                      <th>Status</th>
                      <th class="center-align" style="padding-right: 24px;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="myHistory.length === 0">
                      <td colspan="7" class="center-align grey-text" style="padding: 30px;">You haven't processed any applications yet.</td>
                    </tr>
                    <tr v-for="application in myHistory" :key="'hist-' + application.id">
                      <td style="padding-left: 24px; white-space: nowrap;">{{ formatDate(application.date_of_filing) }}</td>
                      <td class="bold">{{ application.employee ? application.employee.first_name : '' }} {{ application.employee ? application.employee.last_name : '' }}</td>
                      <td>{{ application.leave_type }}</td>
                      <td>{{ application.inclusive_dates }}</td>
                      <td class="grey-text text-darken-1" style="font-size: 0.9rem;">{{ application.current_stage }}</td>
                      <td>
                        <div class="chip bold" 
                             :class="{
                               'green lighten-4 green-text text-darken-4': application.status === 'Approved',
                               'blue lighten-4 blue-text text-darken-4': application.status === 'In Progress',
                               'red lighten-4 red-text text-darken-4': application.status === 'Disapproved',
                               'orange lighten-4 orange-text text-darken-4': application.status === 'Returned'
                             }" 
                             style="border-radius: 4px; margin: 0;">
                          {{ application.status }}
                        </div>
                      </td>
                      <td class="center-align" style="padding-right: 24px; white-space: nowrap;">
                        
                        <button @click="viewPdf(application.id)" class="btn-small blue darken-2 waves-effect waves-light tooltipped" data-position="top" data-tooltip="View Record" style="margin-right: 5px; border-radius: 4px; padding: 0 10px;">
                          <i class="material-icons">visibility</i>
                        </button>
                        <button @click="downloadPdf(application)" class="btn-small blue-grey darken-2 waves-effect waves-light tooltipped" data-position="left" data-tooltip="Download PDF Form" style="border-radius: 4px; padding: 0 10px;">
                          <i class="material-icons">picture_as_pdf</i>
                        </button>

                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- MOBILE CARDS (MY HISTORY) -->
              <div class="hide-on-large-only" style="padding: 15px;">
                <div v-if="myHistory.length === 0" class="center-align grey-text" style="padding: 30px; border: 1px dashed #ccc; border-radius: 8px;">
                  You haven't processed any applications yet.
                </div>
                
                <div v-else style="display: flex; flex-direction: column; gap: 15px;">
                  <div v-for="application in myHistory" :key="'mob-hist-' + application.id" class="card z-depth-0" style="margin: 0; border-radius: 8px; border: 1px solid #e0e0e0;">
                    <div class="card-content" style="padding: 15px;">
                      
                      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px;">
                        <div class="valign-wrapper bold" style="flex: 1; padding-right: 10px;">
                          <i v-if="application.leave_type.includes('Vacation')" class="material-icons green-text text-darken-1" style="margin-right: 8px;">work_outline</i>
                          <i v-else-if="application.leave_type.includes('Sick')" class="material-icons blue-text text-darken-1" style="margin-right: 8px;">local_hospital</i>
                          <i v-else class="material-icons orange-text text-darken-1" style="margin-right: 8px;">event</i>
                          <span style="font-size: 1.05rem; color: #1565c0; line-height: 1.2;">{{ application.leave_type }}</span>
                        </div>
                        <div class="chip bold" 
                             :class="{
                               'green lighten-4 green-text text-darken-4': application.status === 'Approved',
                               'blue lighten-4 blue-text text-darken-4': application.status === 'In Progress',
                               'red lighten-4 red-text text-darken-4': application.status === 'Disapproved',
                               'orange lighten-4 orange-text text-darken-4': application.status === 'Returned'
                             }" 
                             style="margin: 0; border-radius: 4px; height: 24px; line-height: 24px; font-size: 0.75rem;">
                          {{ application.status }}
                        </div>
                      </div>
                      
                      <p style="margin-bottom: 5px; font-size: 0.95rem;"><b>Applicant: </b> <span style="font-weight: 500;">{{ application.employee ? application.employee.first_name : '' }} {{ application.employee ? application.employee.last_name : '' }}</span></p>
                      
                      <div class="grey-text text-darken-2 valign-wrapper" style="font-size: 0.95rem; margin-bottom: 5px;">
                        <i class="material-icons left" style="font-size: 1.1rem; margin-right: 5px; color: #757575;">date_range</i> 
                        {{ application.inclusive_dates }}
                      </div>

                      <div class="grey-text text-darken-2 valign-wrapper" style="font-size: 0.9rem;">
                        <i class="material-icons left" style="font-size: 1.1rem; margin-right: 5px; color: #757575;">pending_actions</i> 
                        Stage: <span style="margin-left: 5px;">{{ application.current_stage }}</span>
                      </div>
                    </div>
                    
                    <div class="card-action" style="padding: 10px 15px; background-color: #f9f9f9; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; display: flex; justify-content: flex-end; gap: 8px;">
                      <button @click="viewPdf(application.id)" class="btn-small blue darken-2 waves-effect waves-light mobile-flex-btn" style="border-radius: 4px; box-shadow: none;">
                        <i class="material-icons left" style="margin-right: 5px; font-size: 1rem;">visibility</i> View
                      </button>
                      <button @click="downloadPdf(application)" class="btn-small blue-grey darken-2 waves-effect waves-light mobile-flex-btn" style="border-radius: 4px; box-shadow: none;">
                        <i class="material-icons left" style="margin-right: 5px; font-size: 1rem;">picture_as_pdf</i> Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  </AdminAppLayout>
</template>

<script>
import AdminAppLayout from '@/views/layout/AdminAppLayout.vue'; 
import api from '../../../axios.js';

export default {
  name: "LeaveApplicationsDashboardView",
  components: { AdminAppLayout },
  data() {
    return {
      loading: true,
      approverQueue: [],
      myHistory: [] 
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },

    async fetchDashboardData() {
      this.loading = true;

      try {
        const response = await api.get('/leave-dashboard');
        this.approverQueue = response.data.action_required_queue;
        this.myHistory = response.data.my_history || response.data.all_applications || []; 
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (window.M) window.M.toast({ html: 'Failed to load data.', classes: 'red' });
      } finally {
        this.loading = false;
        
        this.$nextTick(() => {
          if (window.M) {
            const tooltips = document.querySelectorAll('.tooltipped');
            window.M.Tooltip.init(tooltips);
            
            const tabsElement = this.$refs.adminTabs;
            if (tabsElement) {
              const tabInstance = window.M.Tabs.init(tabsElement);
              if (tabInstance) {
                tabInstance.select('pending-queue');
              }
            }
          }
        });
      }
    },

    async viewPdf(applicationId) {
      try {
        const response = await api.get(`/leave-applications/${applicationId}/pdf`, { responseType: 'blob' });
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } catch (error) {
        console.error("Error fetching PDF:", error);
        if (window.M) {
          window.M.toast({ html: 'Failed to load PDF.', classes: 'red' });
        } else {
          alert("Failed to load PDF.");
        }
      }
    },

    async downloadPdf(app) {
      try {
        if (window.M) window.M.toast({ html: 'Downloading PDF...', classes: 'blue' });
        
        const response = await api.get(`/leave-applications/${app.id}/pdf`, {
          responseType: 'blob'
        });
        
        const cleanDate = app.date_of_filing ? app.date_of_filing.replace(/-/g, '_') : 'Date';
        const employeeName = app.employee ? app.employee.last_name : app.last_name;
        
        const properFileName = `CS_Form_6_${employeeName}_${cleanDate}.pdf`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        link.setAttribute('download', properFileName); 
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error("Error downloading PDF:", error);
        if (window.M) window.M.toast({ html: 'Failed to download PDF.', classes: 'red' });
      }
    }
  },
  mounted() {
    this.fetchDashboardData();
  }
};
</script>

<style scoped>
.responsive-table td {
  vertical-align: middle;
}
.tabs .tab a:hover, .tabs .tab a.active {
  background-color: rgba(40, 53, 147, 0.05); 
}
.tabs .indicator {
  background-color: #283593; 
}

@keyframes shine {
  to {
    background-position-x: -200%;
  }
}

/* Mobile Responsive Adjustments */
@media only screen and (max-width: 768px) {
  .mobile-full-btn {
    width: 100%;
    justify-content: center;
  }
  .mobile-flex-btn {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .tabs .tab a {
    font-size: 0.85rem !important;
  }
}
</style>